/**
 * /scenarios page coordinator.
 *
 * Scope derivation (fiscalYear + departmentCode):
 *   1. Read `fy` and `department` from URL search params (wouter useSearch).
 *   2. Fall back to DEFAULT_FISCAL_YEAR (2026) and DEFAULT_DEPARTMENT_CODE ("643")
 *      when those params are absent.
 *
 * All child components receive `scope` as a prop; nothing is hard-coded downstream.
 */

import { useMemo, useState } from "react";
import { useSearch } from "wouter";
import { AlertCircle, GitBranch, Loader2, Plus, RefreshCw, Scale } from "lucide-react";
import {
  useListScenarioQuestionTemplates,
  useListScenarios,
  getListScenariosQueryKey,
} from "@workspace/api-client-react";
import type { Scenario } from "@workspace/api-client-react";
import { Shell } from "@/components/tekledger-ui";
import {
  DEFAULT_FISCAL_YEAR,
  DEFAULT_DEPARTMENT_CODE,
} from "@/components/scenarios/types";
import { useWorkspaceCompany } from "@/hooks/use-workspace-company";
import type { ScenarioScope } from "@/components/scenarios/types";
import { ScenarioCard } from "@/components/scenarios/shared";
import { CreateScenarioWizard } from "@/components/scenarios/creation";
import { ScenarioDetailPanel } from "@/components/scenarios/detail";
import { ComparePanel } from "@/components/scenarios/comparison";
import { LicenseEconomicsSection } from "@/components/license-economics/index";

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ScenariosPage() {
  const { companyId } = useWorkspaceCompany();
  // ── Scope from URL search params ─────────────────────────────────────────────
  const searchString = useSearch();
  const scope: ScenarioScope = useMemo(() => {
    const p = new URLSearchParams(searchString);
    const fy = p.get("fy");
    const dept = p.get("department");
    return {
      companyId,
      fiscalYear: fy ? Number(fy) : DEFAULT_FISCAL_YEAR,
      departmentCode: dept ?? DEFAULT_DEPARTMENT_CODE,
    };
  }, [companyId, searchString]);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [showWizard, setShowWizard] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCompare, setShowCompare] = useState(false);

  // ── Data ─────────────────────────────────────────────────────────────────────
  const { data: templates, isLoading: templatesLoading } =
    useListScenarioQuestionTemplates();

  const {
    data: scenarios,
    isLoading: scenariosLoading,
    isError: scenariosError,
    refetch: refetchScenarios,
    isFetching,
  } = useListScenarios(scope, {
    query: { queryKey: getListScenariosQueryKey(scope) },
  });

  const isLoading = templatesLoading || scenariosLoading;
  const scenarioList = scenarios ?? [];
  const draftScenarios = scenarioList.filter((s) => s.status === "draft");
  const promotedScenarios = scenarioList.filter((s) => s.status === "promoted");

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleWizardSuccess = (scenario: Scenario) => {
    setShowWizard(false);
    setSelectedId(scenario.scenarioId);
  };

  return (
    <Shell active="Scenarios">
      <section className="tl-content">
        {/* Page header */}
        <div className="sc-page-head" data-testid="scenarios-page-head">
          <div>
            <div className="eyebrow">Management laboratory</div>
            <h1 data-testid="text-page-title">Scenarios</h1>
            <p className="sub">
              Isolate assumptions against the current authoritative Forecast for
              FY{scope.fiscalYear} · dept {scope.departmentCode}. Add structured
              drivers, inspect monthly and FY impacts, and promote a scenario
              into a controlled working Forecast for Review.
            </p>
          </div>
          <div className="sc-page-head__actions">
            {scenarioList.length >= 2 && (
              <button
                className="tl-btn"
                onClick={() => setShowCompare((v) => !v)}
                aria-expanded={showCompare}
                data-testid="button-open-compare"
              >
                <Scale size={13} />
                Compare
              </button>
            )}
            <button
              className="tl-btn primary"
              onClick={() => setShowWizard(true)}
              data-testid="button-new-scenario"
            >
              <Plus size={13} />
              New scenario
            </button>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="sc-page-loading" data-testid="scenarios-loading">
            <Loader2 size={18} className="animate-spin" />
            <span>Loading scenarios…</span>
          </div>
        )}

        {/* Error */}
        {scenariosError && !isLoading && (
          <div
            className="sc-form-error sc-form-error--page"
            role="alert"
            data-testid="scenarios-error"
          >
            <AlertCircle size={15} />
            <div>
              <strong>Could not load scenarios.</strong>
              <button
                className="tl-btn"
                onClick={() => refetchScenarios()}
                disabled={isFetching}
                style={{ marginLeft: 12 }}
              >
                <RefreshCw
                  size={12}
                  className={isFetching ? "animate-spin" : ""}
                />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Compare panel */}
        {showCompare && scenarioList.length > 0 && (
          <ComparePanel
            scenarios={scenarioList}
            onClose={() => setShowCompare(false)}
          />
        )}

        {/* Create wizard */}
        {showWizard && templates && (
          <div className="sc-wizard-container">
            <CreateScenarioWizard
              templates={templates}
              scope={scope}
              onSuccess={handleWizardSuccess}
              onCancel={() => setShowWizard(false)}
            />
          </div>
        )}

        {/* Empty state */}
        {!isLoading &&
          !scenariosError &&
          scenarioList.length === 0 &&
          !showWizard && (
            <div
              className="sc-empty-page"
              data-testid="scenarios-empty-state"
            >
              <GitBranch size={32} />
              <h3>No scenarios yet</h3>
              <p>
                Create your first scenario to begin modeling assumptions against
                the current Forecast for FY{scope.fiscalYear} · dept{" "}
                {scope.departmentCode}. Start with a management question to keep
                assumptions isolated and evidence visible.
              </p>
              <button
                className="tl-btn primary"
                onClick={() => setShowWizard(true)}
                data-testid="button-create-first-scenario"
              >
                <Plus size={13} />
                Create first scenario
              </button>
            </div>
          )}

        {/* Main layout: list + detail */}
        {!isLoading && scenarioList.length > 0 && !showWizard && (
          <div className="sc-workspace" data-testid="scenarios-workspace">
            {/* Left: scenario list */}
            <div className="sc-list" data-testid="scenarios-list">
              {draftScenarios.length > 0 && (
                <div className="sc-list__group">
                  <div className="sc-list__group-label">Draft</div>
                  {draftScenarios.map((s) => (
                    <ScenarioCard
                      key={s.scenarioId}
                      scenario={s}
                      isSelected={selectedId === s.scenarioId}
                      onClick={() =>
                        setSelectedId(
                          selectedId === s.scenarioId ? null : s.scenarioId
                        )
                      }
                    />
                  ))}
                </div>
              )}

              {promotedScenarios.length > 0 && (
                <div className="sc-list__group">
                  <div className="sc-list__group-label">Promoted</div>
                  {promotedScenarios.map((s) => (
                    <ScenarioCard
                      key={s.scenarioId}
                      scenario={s}
                      isSelected={selectedId === s.scenarioId}
                      onClick={() =>
                        setSelectedId(
                          selectedId === s.scenarioId ? null : s.scenarioId
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Right: detail panel */}
            {selectedId ? (
              <ScenarioDetailPanel
                scenarioId={selectedId}
                templates={templates ?? []}
                scope={scope}
                onClose={() => setSelectedId(null)}
              />
            ) : (
              <div
                className="sc-detail-empty"
                data-testid="detail-panel-empty"
              >
                <GitBranch size={22} />
                <p>
                  Select a scenario to inspect its drivers, impacts, and
                  reconciliation.
                </p>
              </div>
            )}
          </div>
        )}

        {!showWizard && (
          <LicenseEconomicsSection
            companyId={scope.companyId}
            fiscalYear={scope.fiscalYear}
            departmentCode={scope.departmentCode}
          />
        )}
      </section>
    </Shell>
  );
}
